import React, { useState, useEffect } from "react";
import { Button, Typography, Table, Sheet, CircularProgress, Box, IconButton, Tooltip, Input } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../Pagination";

import { ParentGuardian, PostgresChangePayload } from "../../types";

import { supabase } from "../../supabaseClient";

import AddEditParentGuardianModal from "../modals/AddEditParentGuardianModal";

const ParentGuardiansTable: React.FC = () => {
  const [parentGuardians, setParentGuardians] = useState<ParentGuardian[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { showToast } = useToast();
  const { openModal } = useModal();

  // Use pagination hook
  const {
    paginatedData: paginatedParentGuardians,
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
  } = usePagination({
    data: parentGuardians,
    searchTerm: searchQuery,
    searchFields: ["first_name", "last_name"],
  });

  // Fetch parent/guardian data
  const fetchParentGuardians = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("parent_guardians")
        .select(`
          id,
          first_name,
          last_name,
          phone_number,
          email_address,
          students_parent_guardians (parent_guardian_id)
        `); // Omitting !inner performs a left join

      if (error) {
        throw error;
      }

      const formattedParentGuardians: ParentGuardian[] = (data || []).map((entry: any) => ({
        id: entry.id,
        first_name: entry.first_name,
        last_name: entry.last_name,
        phone_number: entry.phone_number,
        email_address: entry.email_address,
        student_count: entry.students_parent_guardians?.length || 0, // Count related students, default to 0
      }));

      setParentGuardians(formattedParentGuardians);
    } catch (error) {
      console.error("Error fetching parent/guardian data:", error);
      showToast("Error fetching parent/guardian data.", "danger", "solid");
    } finally {
      setLoading(false);
    }
  };

  // Fetch a single parent/guardian relationship with details and student count
  const fetchParentGuardianWithRelations = async (parentGuardianId: string) => {
    try {
      const { data, error } = await supabase
        .from("parent_guardians")
        .select(`
          id,
          first_name,
          last_name,
          phone_number,
          email_address,
          students_parent_guardians (
            student_id
          )
        `)
        .eq("id", parentGuardianId) // Ensure this filters correctly
        .single(); // Ensure only one record is returned

      if (error) {
        console.error("Error fetching parent/guardian with relations:", error);
        throw error;
      }

      if (!data) {
        console.error("Parent/guardian data is missing or invalid:", data);
        return null;
      }

      const parentGuardian: any = data;

      return {
        id: parentGuardian.id,
        first_name: parentGuardian.first_name,
        last_name: parentGuardian.last_name,
        phone_number: parentGuardian.phone_number,
        email_address: parentGuardian.email_address,
        student_count: parentGuardian.students_parent_guardians?.length || 0, // Count the related students
      } as ParentGuardian;
    } catch (error) {
      console.error("Error fetching parent/guardian with relations:", error);
      return null;
    }
  };

  const handleParentGuardianChange = async (payload: PostgresChangePayload<any>) => {
    if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
      // Fetch the updated parent/guardian with relations
      const updatedParentGuardian = await fetchParentGuardianWithRelations(payload.new.id);

      if (updatedParentGuardian) {
        setParentGuardians((prevParentGuardians) => {
          // Replace the existing parent/guardian or add it if it's a new entry
          const guardianIndex = prevParentGuardians.findIndex((pg) => pg.id === updatedParentGuardian.id);
          if (guardianIndex !== -1) {
            const updatedGuardians = [...prevParentGuardians];
            updatedGuardians[guardianIndex] = updatedParentGuardian;
            return updatedGuardians;
          } else {
            return [...prevParentGuardians, updatedParentGuardian];
          }
        });
      }
    } else if (payload.eventType === "DELETE") {
      // Remove the deleted parent/guardian
      setParentGuardians((prevParentGuardians) =>
        prevParentGuardians.filter((pg) => pg.id !== payload.old.parent_guardian_id)
      );
    }
  };

  const handleAddParentGuardian = () => {
    openModal(
      <AddEditParentGuardianModal />,
      {}
    );
  };

  const handleEditParentGuardian = (parentGuardian: ParentGuardian) => {
    openModal(
      <AddEditParentGuardianModal parentGuardian={parentGuardian} />,
      {}
    );
  };

  const handleRemoveParentGuardian = async (parentGuardianId: string) => {
    try {
      // Delete the parent/guardian record
      const { error } = await supabase
        .from("parent_guardians")
        .delete()
        .eq("id", parentGuardianId);

      if (error) {
        console.error("Error deleting from parent_guardians:", error);
        throw new Error(error?.message || "No record was deleted in parent_guardians.");
      }

      console.log(`Deleted record from parent_guardians, with cascading to students_parent_guardians.`);

      showToast("Parent/Guardian removed successfully.", "success", "solid");

      // Update the state by removing the deleted parent/guardian
      setParentGuardians((prev) => prev.filter((pg) => pg.id !== parentGuardianId));
    } catch (error) {
      console.error("Error removing parent/guardian:", error);
      showToast("Error removing parent/guardian. Please try again.", "danger", "solid");
    }
  };

  useEffect(() => {
    fetchParentGuardians();

    const subscription = supabase
      .channel("public:parent_guardians")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "parent_guardians" },
        async (payload) => {
          console.log("Change received!", payload);
          await handleParentGuardianChange(payload as any);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
    /* eslint-disable-next-line */
  }, []);

  return (
    <React.Fragment>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} gap={2}>
        <Input
          placeholder="Search parents/guardians..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startDecorator={<SearchIcon />}
          sx={{ width: 300 }}
        />
        <Button startDecorator={<AddIcon />} onClick={handleAddParentGuardian} sx={{ ml: 2, minWidth: 180 }}>
          Add Parent / Guardian
        </Button>
      </Box>
      <Sheet variant="outlined" sx={{ width: "100%", borderRadius: "sm", overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            sx={{ width: "100%" }}
          >
            <CircularProgress />
          </Box>
        ) : paginatedParentGuardians.length > 0 ? (
          <>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Email Address</th>
                  <th># of Students</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedParentGuardians.map((pg) => (
                  <tr key={pg.id}>
                    <td>
                      <Typography>
                        {pg.first_name} {pg.last_name}
                      </Typography>
                    </td>
                    <td>
                      <Typography>{pg.email_address}</Typography>
                    </td>
                    <td>
                      <Typography>{pg.phone_number}</Typography>
                    </td>
                    <td>
                      <Typography>{pg.student_count}</Typography>
                    </td>
                    <td>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit Parent / Guardian">
                          <IconButton onClick={() => handleEditParentGuardian(pg)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove Parent / Guardian">
                          <IconButton onClick={() => handleRemoveParentGuardian(pg.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              rowsPerPage={rowsPerPage}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px" sx={{ width: "100%" }}>
            <Typography level="body-lg" color="neutral">
              {searchQuery.trim() ? "No parents/guardians found matching your search." : "No Parents / Guardians"}
            </Typography>
          </Box>
        )}
      </Sheet>
    </React.Fragment>
  );
};

export default ParentGuardiansTable;
